class SearchInput {
  constructor({ input, loader, results, url, idKey, labelKey, selectedMake, onSelect, alwaysShowAll=false }) {
    this.input = input;
    this.loader = loader;
    this.resultsContainer = results;
    this.url = url;
    this.selectedMake = selectedMake;
    this.onSelect = onSelect;
    this.alwaysShowAll = alwaysShowAll

    this.idKey = idKey;
    this.labelKey = labelKey;
    this.options = [];
    this.selectedLabel = '';

    this._onInput = this._onInput.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onResultsClick = this._onResultsClick.bind(this);

    this.input.addEventListener('input', this._onInput);
    this.input.addEventListener('blur', this._onBlur);
    this.resultsContainer.addEventListener('click', this._onResultsClick);
    this._loadOptions();
  }

  /** Removes all listeners this instance attached. */
  destroy() {
    this.input.removeEventListener('input', this._onInput);
    this.input.removeEventListener('blur', this._onBlur);
    this.resultsContainer.removeEventListener('click', this._onResultsClick);
  }

  setNewUrl(url) {
    this.url = url;
    this._loadOptions();
  }

  async _loadOptions() {
    this._setLoading(true);

    try {
      const response = await fetch(this.url);
      if (!response.ok) throw new Error(`Failed to load search data: ${response.status}`);

      const data = await response.json();
      this.options = Array.isArray(data) ? data : [];
      if(this.selectedMake) {
        const selectedMakeData = this.options.find(({label}) => label === this.selectedMake)
        if(selectedMakeData){
          const { makeId, label } = selectedMakeData;
          this.selectedLabel = label;
          this.input.value = label;
          this.onSelect({ id: makeId, label })
        }
      }
    } catch (error) {
      this.options = [];
      console.log(error)
    } finally {
      this._setLoading(false);
      if(this.alwaysShowAll) {
        this._onInput({target: {value: ''}});
      }
    }
  }

  _onInput(event) {
    const value = event.target.value;
    if(this.alwaysShowAll) {
      this._search(value)
    } else if (value.length > 1) {
      this._search(value);
    } else {
      this._clearResults();
    }
  }

  _onBlur() {
    // Delay so a click on a result item (which fires after blur) can still register.
    setTimeout(() => {
      this._clearResults();
      this.input.value = this.selectedLabel;
    }, 150);
  }

  _onResultsClick(event) {
    const item = event.target.closest('[data-id]');
    if (!item) return;

    const { id, label } = item.dataset;
    this.selectedLabel = label;
    this.input.value = label;
    this._clearResults();

    if (typeof this.onSelect === 'function') {
      this.onSelect({ id, label });
    }
  }

  _search(query) {
    const normalizedQuery = query.trim().toLowerCase();
    const results = this.options.filter((entry) =>
      entry[this.labelKey].toLowerCase().includes(normalizedQuery)
    ).sort((a,b) => {
      if(a[this.labelKey] > b[this.labelKey])  {
        return 1
      } else {
        return -1
      }
    });
    this._renderResults(results);
  }

  _setLoading(isLoading) {
    if (isLoading) {
      this.input.setAttribute('disabled', '');
      this.loader.classList.remove('hidden');
    } else {
      this.input.removeAttribute('disabled');
      this.loader.classList.add('hidden');
    }
  }

  _renderResults(results) {
    this.resultsContainer.innerHTML = '';
    if (!Array.isArray(results) || results.length === 0) return;

    const fragment = document.createDocumentFragment();
    results.forEach((result) => {
      const item = document.createElement('div');
      item.textContent = result[this.labelKey];
      item.dataset.id = result[this.idKey];
      item.dataset.label = result[this.labelKey];
      item.classList.add('vrm-autocomplete__item');
      fragment.appendChild(item);
    });
    this.resultsContainer.appendChild(fragment);
  }

  _clearResults() {
    this.resultsContainer.innerHTML = '';
  }
}

class YearSearchInput {
  constructor({ input, resultsContainer, onSelect, currentYear }) {
    this.input = input;
    this.resultsContainer = resultsContainer;
    this.onSelect = onSelect;
    this.currentYear = currentYear || new Date().getFullYear();

    this.selectedLabel = '';

    this._onInput = this._onInput.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onResultsClick = this._onResultsClick.bind(this);

    this.input.addEventListener('input', this._onInput);
    this.input.addEventListener('focus', this._onFocus);
    this.input.addEventListener('blur', this._onBlur);
    this.resultsContainer.addEventListener('click', this._onResultsClick);
  }

  /** Removes all listeners this instance attached. */
  destroy() {
    this.input.removeEventListener('input', this._onInput);
    this.input.removeEventListener('focus', this._onFocus);
    this.input.removeEventListener('blur', this._onBlur);
    this.resultsContainer.removeEventListener('click', this._onResultsClick);
  }

  _onFocus(event) {
    this._search(event.target.value);
  }

  _onInput(event) {
    this._search(event.target.value);
  }

  _onBlur() {
    // Delay so a click on a result item (which fires after blur) can still register.
    setTimeout(() => {
      this._clearResults();
      this.input.value = this.selectedLabel;
    }, 150);
  }

  _onResultsClick(event) {
    const item = event.target.closest('[data-year]');
    if (!item) return;

    const year = item.dataset.year;
    this.selectedLabel = year;
    this.input.value = year;
    this._clearResults();

    if (typeof this.onSelect === 'function') {
      this.onSelect({ year: Number(year) });
    }
  }

  _search(rawValue) {
    const years = this._getYearRange(rawValue);
    if (!years) {
      this._clearResults();
      return;
    }
    this._renderResults(years);
  }

  /**
   * Turns the typed value into an inclusive range of 11 years ending at
   * the largest year that starts with the typed digits.
   */
  _getYearRange(rawValue) {
    const value = rawValue.trim();

    if (value === '') {
      return this._buildRange(this.currentYear);
    }

    // Only accept up to 4 digits; anything else has no sensible "largest year".
    if (!/^\d{1,4}$/.test(value)) return null;

    if (/^\d{4}$/.test(value)) {
      return [parseInt(value)];
    }
    const computedEndYear = Number(value.padEnd(4, '9'))
    const endYear = computedEndYear > new Date().getFullYear() ? 2026 : computedEndYear;
    return this._buildRange(endYear);
  }

  _buildRange(endYear) {
    const years = [];
    for (let year = endYear - 10; year <= endYear; year++) {
      years.push(year);
    }
    return years.reverse();
  }

  _renderResults(years) {
    this.resultsContainer.innerHTML = '';

    const fragment = document.createDocumentFragment();
    years.forEach((year) => {
      const item = document.createElement('div');
      item.textContent = year;
      item.dataset.year = year;
      item.classList.add('search-result-item');
      fragment.appendChild(item);
    });
    this.resultsContainer.appendChild(fragment);
  }

  _clearResults() {
    this.resultsContainer.innerHTML = '';
  }
}

class VrmSearchMakes extends HTMLElement {
  makes = []
  selectedMakeId = null
  selectedYear = null
  selectedModelUrl = null

  constructor() {
    super();
  }

  connectedCallback() {
    this.modal = document.querySelector('.vrm-modal__no')
    this.form = this.querySelector('[data-vrm-form-makes]')
    this.submitButton = this.querySelector('[data-vrm-search-button]')
    this.errorContainer = this.querySelector('[data-vrm-search-error]')
    
    this.makeElements = getElementsForSearchInput('make', this)
    this.yearElements = getElementsForSearchInput('year', this)
    this.modelElements = getElementsForSearchInput('model', this)

    this.form.addEventListener('submit', (e) => this.handleSubmit(e))

    const params = new URLSearchParams(window.location.search);
    const paramList = Array.from(params.entries())
    const selectedMake = paramList.length ? Array.from(params.entries())[0][1] : undefined

    this.makesSearch = new SearchInput({ 
      ...this.makeElements,
      url: 'https://api.wiperz.co.uk/car/makes',
      idKey: 'makeId',
      labelKey: 'label',
      selectedMake,
      onSelect: ({id, label}) => {
        this.selectedMakeId = id
        this.onSelectMakeOrYear()
      }
    })

    this.yearSearch = new YearSearchInput({
      input: this.yearElements.input,
      resultsContainer: this.yearElements.results,
      onSelect: ({ year }) => {
        this.selectedYear = year
        this.onSelectMakeOrYear(null, year)
      }
    });

    this.observer = getClassChangeObserver(this.modal, (classList) => {
      if(this.makes.length === 0 && !classList.some(cls => cls === 'hidden')) {
        this.init()
      }
    })
  }

  disconnectedCallback() {
    // cleanup on DOM detach
  }

  async init() {
  }

  onSelectMakeOrYear() {
    if(this.selectedYear && this.selectedMakeId) {
      if(!this.modelSearch) {
        this.modelSearch = new SearchInput({ 
          ...this.modelElements,
          url: `https://api.wiperz.co.uk/car/models/${this.selectedMakeId}/${this.selectedYear}`,
          idKey: 'url',
          labelKey: 'model',
          onSelect: ({id, label}) => {
            this.selectedModelUrl = id
          },
          alwaysShowAll: true,
        })
      } else {
        this.modelSearch.setNewUrl(`https://api.wiperz.co.uk/car/models/${this.selectedMakeId}/${this.selectedYear}`)
      }
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    if(!this.selectedModelUrl) {
      return
    }
    window.location.href = this.selectedModelUrl
  }
}

if (!customElements.get('vrm-search-makes')) {
  customElements.define(
    'vrm-search-makes',
    VrmSearchMakes
  );
}

function getClassChangeObserver(targetNode, classChanged) {  
  const callback = (mutationList, observer) => {
    for (const mutation of mutationList) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        classChanged(Array.from(mutation.target.classList))
      }
    }
  }
  
  const observer = new MutationObserver(callback)
  observer.observe(targetNode, { attributes: true })
  return observer
}

function getElementsForSearchInput(name, context) {
  const input = context.querySelector(`[data-vrm-search-${name}]`);
  const loader = context.querySelector(`.vrm-form-${name}.icon-container`)
  const results = context.querySelector(`[data-${name}-search-results]`)
  return { input, loader, results }
}